import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { ai, flash, scheduleLinks, todayISO } from "./lib";
import { StepShell, AiButton, desireContext, joinWords } from "./FlowKit";

export const downloadNode = async (node, filename, bg = "#EFEEE9") => {
  const canvas = await html2canvas(node, {
    backgroundColor: bg,
    scale: 2,
    useCORS: true,
    onclone: (doc) => {
      const st = doc.createElement("style");
      st.textContent = "*{animation:none!important;transition:none!important;opacity:1!important}:root{--sage:#46594A;--muted:#55594F}";
      doc.head.appendChild(st);
      doc.querySelectorAll("input[type=text], input:not([type]), textarea").forEach((el) => {
        const span = doc.createElement("div");
        span.textContent = el.value;
        span.style.cssText = "flex:1;display:block;font-family:Manrope,sans-serif;font-style:normal;font-size:15px;line-height:1.5;color:#262A27;padding:8px 2px;border-bottom:1px solid rgba(38,42,39,0.12)";
        el.replaceWith(span);
      });
    },
  });
  const a = document.createElement("a");
  a.download = filename;
  a.href = canvas.toDataURL("image/png");
  a.click();
};

export const becomeText = (p) =>
  p.desires
    .filter((d) => (p.becomeAnswers?.[d.x] || "").trim())
    .map((d) => `For ${d.x} (to feel ${d.y}): ${p.becomeAnswers[d.x].trim()}`)
    .join("\n");

export const StepBecome = ({ p, update, go }) => {
  const cardRef = useRef(null);
  const [note, setNote] = useState("");
  const answers = p.becomeAnswers || {};
  const groups = p.groups || [];
  const setAnswer = (x, v) => update({ becomeAnswers: { ...answers, [x]: v } });
  const run = async () => {
    const text = becomeText(p);
    if (!text) return flash(setNote, "Write about at least one first.");
    const r = await ai("priorities", text, desireContext(p.desires));
    update({ identity: r.identity, groups: r.groups || [], identityText: text });
  };
  const setItem = (g, i, v) =>
    update({ groups: groups.map((gr, j) => (j === g ? { ...gr, priorities: gr.priorities.map((x, k) => (k === i ? v : x)) } : gr)) });
  const save = async () => {
    await downloadNode(cardRef.current, "my-daily-priorities.png", "#C9D3C6");
    flash(setNote, "Saved to your downloads.");
  };

  return (
    <StepShell n={3} label="Being vs. wanting" title="Who do you have to be in order to make that inevitable?">
      <p>What do you do daily?</p>
      <div className="area-list">
        {p.desires.map((d, i) => (
          <div className="area-row" key={`${d.x}-${i}`} data-testid={`become-row-${i}`}>
            <div className="clear-q">To create {d.x}</div>
            <textarea className="field-area" value={answers[d.x] || ""} onChange={(e) => setAnswer(d.x, e.target.value)} placeholder="Every day, I..." data-testid={`become-input-${i}`} />
          </div>
        ))}
      </div>
      <AiButton onRun={run} label={groups.length ? "Rewrite my list" : "Create my daily priorities"} testid="step3-generate-btn" />
      <div className="save-note" data-testid="step3-note">{note}</div>

      {groups.length > 0 && (
        <>
          <div className="priority-card" ref={cardRef} data-testid="priority-card">
            <div className="label">My daily priorities</div>
            <p className="identity-line" data-testid="identity-line">{p.identity}</p>
            {groups.map((g, gi) => (
              <div className="priority-group" key={gi} data-testid={`priority-group-${gi}`}>
                <div className="priority-desire">{g.desire}</div>
                <ol className="priority-list">
                  {g.priorities.map((x, i) => (
                    <li key={i}>
                      <span>0{i + 1}</span>
                      <input value={x} onChange={(e) => setItem(gi, i, e.target.value)} data-testid={`priority-input-${gi}-${i}`} />
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
          <div className="btn-row">
            <button className="mono-btn calendar-btn" onClick={save} data-testid="save-priorities-btn">Save my list</button>
            <button className="mono-btn save-btn" onClick={() => go(4)} data-testid="step3-continue-btn">Continue</button>
          </div>
        </>
      )}
    </StepShell>
  );
};

const Schedule = ({ p, update }) => {
  const [note, setNote] = useState("");
  const s = { action: "", date: todayISO(), time: "09:00", ...(p.scheduled || {}) };
  const set = (patch) => update({ scheduled: { ...s, ...patch } });
  const links = scheduleLinks(s.action.trim(), s.date, s.time);
  const ok = () => {
    if (!s.action.trim()) { flash(setNote, "Type the action you'll take first."); return false; }
    if (!s.date) { flash(setNote, "Pick a date first."); return false; }
    return true;
  };
  return (
    <div className="clear-card" data-testid="schedule-card">
      <div className="clear-q">Choose one fearless action and give it a time. In your own words.</div>
      <textarea rows={2} className="field-area schedule-action" value={s.action} onChange={(e) => set({ action: e.target.value, done: false })} placeholder="The fearless action I'll take is..." data-testid="schedule-action-input" />
      <div className="pick-row">
        {p.fears.map((f, i) => (
          <button key={i} className="pick-chip" onClick={() => set({ action: f.step, done: false })} data-testid={`schedule-pick-${i}`}>
            + {f.step}
          </button>
        ))}
      </div>
      <div className="date-row">
        <label htmlFor="fearDate">On</label>
        <input id="fearDate" type="date" value={s.date} onChange={(e) => set({ date: e.target.value })} data-testid="schedule-date-input" />
        <input type="time" value={s.time} onChange={(e) => set({ time: e.target.value })} className="time-input" data-testid="schedule-time-input" />
      </div>
      <div className="btn-row">
        <button className="mono-btn calendar-btn mt-4" onClick={() => { if (!ok()) return; links.download(); set({ done: true }); flash(setNote, "Added, check your downloads."); }} data-testid="schedule-ics-btn">Add to calendar</button>
        <a
          className="mono-btn calendar-btn mt-4"
          href={links.google}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => { if (!ok()) { e.preventDefault(); return; } set({ done: true }); }}
          data-testid="schedule-google-btn"
        >
          Google Calendar
        </a>
      </div>
      <div className="save-note" data-testid="schedule-note">{note}</div>
    </div>
  );
};

const withClarify = (p) =>
  p.fearClarify && p.fearClarifyAnswer.trim()
    ? `${p.fearText}\n\nWhen asked "${p.fearClarify}", they answered:\n${p.fearClarifyAnswer}`
    : p.fearText;

export const StepFear = ({ p, update, onReveal }) => {
  const [note, setNote] = useState("");
  const run = async () => {
    if (p.fearText.trim().length < 3) return flash(setNote, "Name the belief first.");
    const r = await ai("fears", withClarify(p), desireContext(p.desires));
    if (r.clarify) update({ fears: [], fearClarify: r.clarify, fearClarifyAnswer: "" });
    else update({ fears: r.fears, fearClarify: "", fearClarifyAnswer: "" });
  };
  return (
    <StepShell n={5} label="Facing your shadow" title="When you picture yourself having fulfilled everything in your vision, what part of you is in disbelief?">
      <p>What inner dialogue is keeping you from this place?</p>
      <textarea className="field-area vision-textarea" value={p.fearText} onChange={(e) => update({ fearText: e.target.value })} placeholder="Part of me believes..." data-testid="fear-textarea" />
      {!p.fearClarify && <AiButton onRun={run} label={p.fears.length ? "Mirror it back again" : "Mirror it back"} testid="face-generate-btn" />}
      <div className="save-note">{note}</div>

      {p.fearClarify && (
        <div className="clear-card clarify-card" data-testid="fear-clarify">
          <div className="label">Stay with it a moment</div>
          <div className="clear-q" data-testid="fear-clarify-question">{p.fearClarify}</div>
          <textarea className="field-area" style={{ minHeight: 90 }} value={p.fearClarifyAnswer} onChange={(e) => update({ fearClarifyAnswer: e.target.value })} placeholder="What I mean is..." data-testid="fear-clarify-input" />
          <AiButton onRun={run} label="Mirror it back" testid="fear-clarify-btn" disabled={!p.fearClarifyAnswer.trim()} />
        </div>
      )}

      {p.fears.length > 0 && (
        <>
          <div className="fear-list" data-testid="fear-list">
            {p.fears.map((f, i) => (
              <div className="fear-card" key={i} data-testid={`fear-card-${i}`}>
                <div className="fear-text">{f.fear}</div>
                <div className="fear-step"><span>Fearless action</span>{f.step}</div>
              </div>
            ))}
          </div>
          <Schedule p={p} update={update} />
          <button className="mono-btn save-btn" onClick={onReveal} data-testid="reveal-protocol-btn">Reveal my protocol</button>
        </>
      )}
    </StepShell>
  );
};

const seekingFor = (desires) => {
  const map = {};
  desires.forEach((d) => {
    const y = d.y.trim().toLowerCase();
    if (y) (map[y] = map[y] || []).push(d.x);
  });
  return Object.entries(map);
};

export const glimmerText = (p) =>
  seekingFor(p.desires)
    .filter(([y]) => (p.glimmers?.[y] || "").trim())
    .map(([y]) => `${y}: ${p.glimmers[y].trim()}`)
    .join("\n");

export const StepAppreciate = ({ p, update, go }) => {
  const [note, setNote] = useState("");
  const seeking = seekingFor(p.desires);
  const glimmers = p.glimmers || {};
  const setGlimmer = (y, v) => {
    const next = { ...glimmers, [y]: v };
    update({ glimmers: next, whereText: glimmerText({ ...p, glimmers: next }) });
  };
  const next = () => {
    if (!glimmerText(p)) return flash(setNote, "Notice at least one glimmer first.");
    go(5);
  };
  return (
    <StepShell n={4} label="Appreciation" title="Where do you already feel glimmers of it?">
      <p>What you are really seeking is a feeling. And it is already here, somewhere.</p>
      <div className="area-list">
        {seeking.map(([y, xs], i) => (
          <div className="area-row glimmer-row" key={y} data-testid={`glimmer-row-${i}`}>
            <div className="glimmer-mirror">
              You are seeking to feel <em>{y}</em>
              <span> through {joinWords(xs)}</span>
            </div>
            <div className="clear-q">Where do you already feel a glimmer of this feeling?</div>
            <textarea className="field-area" value={glimmers[y] || ""} onChange={(e) => setGlimmer(y, e.target.value)} placeholder="I already feel this in..." data-testid={`glimmer-input-${i}`} />
          </div>
        ))}
      </div>
      <div className="clear-q" style={{ marginTop: 40 }}>How can you appreciate these moments more? Celebrate them?</div>
      <textarea className="field-area" style={{ minHeight: 100 }} value={p.howText} onChange={(e) => update({ howText: e.target.value })} placeholder="I'll celebrate them by..." data-testid="how-textarea" />
      <button className="mono-btn release-btn" onClick={next} data-testid="appreciate-continue-btn">Continue</button>
      <div className="save-note" data-testid="appreciate-note">{note}</div>
    </StepShell>
  );
};
